import { createAsyncThunk } from '@reduxjs/toolkit';

import { ButtonService } from '../../elements/button.service';
import type { ApiError } from '../../models/api-error.enum';
import { parseApiError } from '../../models/api-error.enum';
import type { AccountSettingsResponse } from '../../models/common.model';
import { CustomerAccountsVersion } from '../../models/common.model';
import { commonService } from '../../services/common.service';
import type { RootState } from '../../store';
import type {
  CheckAuthenticationRequest,
  CheckAuthenticationResponse,
  CustomerResponse,
} from '../models/auth.model';
import { AccountType } from '../models/auth.model';
import { authenticationFlowService } from '../services/authentication-flow.service';
import { customerAuthService } from '../services/customer-auth.service';

const buttonService = new ButtonService();

export const checkAuthentication = createAsyncThunk<
  CheckAuthenticationResponse,
  CheckAuthenticationRequest,
  {
    rejectValue: ApiError;
  }
>('auth/checkAuthentication', async (payload, { rejectWithValue }) => {
  try {
    return await customerAuthService.checkAuthentication(payload);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const initializeCustomer = createAsyncThunk<
  CustomerResponse,
  void,
  {
    rejectValue: ApiError;
  }
>('auth/initializeCustomer', async (_, { rejectWithValue }) => {
  try {
    return await customerAuthService.initializeCustomer();
  } catch (error) {
    console.error('Failed to initialize customer:', error);
    return rejectWithValue(parseApiError(error));
  }
});

export const checkAccountType = createAsyncThunk<
  AccountType,
  void,
  {
    rejectValue: ApiError;
  }
>('auth/checkAccountType', async (_, { rejectWithValue }) => {
  try {
    const customerAccountSettings = await commonService.getCustomerAccountsSettings();

    let accountType = AccountType.NEW;
    if (customerAccountSettings?.customerAccountsVersion === CustomerAccountsVersion.Classic) {
      accountType = AccountType.LEGACY;
    }
    return accountType;
  } catch (error) {
    console.error('[checkAccountType] Account type check failed:', error);
    return rejectWithValue(parseApiError(error));
  }
});

export const loadAccountSettings = createAsyncThunk<
  AccountSettingsResponse,
  void,
  {
    rejectValue: ApiError;
  }
>('auth/loadAccountSettings', async (_, { rejectWithValue }) => {
  try {
    return await commonService.getAccountSettings();
  } catch (error) {
    console.error('Failed to get account settings:', error);
    return rejectWithValue(parseApiError(error));
  }
});

export const attachAuthElements = createAsyncThunk<void, void, { state: RootState }>(
  'auth/attachAuthElements',
  async (_, { dispatch }) => {
    await dispatch(attachLogoutButton());
    await dispatch(attachLogoutSection());
    await dispatch(attachLoginButton());
    await dispatch(attachLoginSection());
    await dispatch(attachWaitingSection());
    await dispatch(checkAccountType());
    await dispatch(checkAuthentication({ loginTypes: [] }));
    authenticationFlowService.processStoredAuthenticationResult();
  }
);

export const attachLogoutButton = createAsyncThunk<
  { success: boolean; button: HTMLElement | null },
  void
>('auth/attachLogoutButton', async () => {
  const element = buttonService.bindLogoutFunction();
  return { success: !!element, button: element };
});

export const attachLogoutSection = createAsyncThunk<
  { success: boolean; sections: HTMLElement[] },
  void,
  { state: RootState }
>('auth/attachLogoutSection', async (_, { getState }) => {
  const elements = buttonService.getAllElements('.single-gate-logout');
  const success = !!elements && elements.length > 0;

  // Handle visibility toggling
  const loggedIn = getState().auth.loggedIn;
  elements?.forEach((section) =>
    buttonService.toggleVisibility(section, !!loggedIn, 'auth/attachLogoutSection')
  );

  return { success, sections: elements || [] };
});

export const attachLoginButton = createAsyncThunk<
  { success: boolean; button: HTMLElement | null },
  void
>('auth/attachLoginButton', async () => {
  const element = buttonService.bindLoginFunction();

  // Handle visibility toggling
  buttonService.toggleVisibility(element, true);

  return { success: !!element, button: element };
});

export const attachLoginSection = createAsyncThunk<
  { success: boolean; sections: HTMLElement[] },
  void
>('auth/attachLoginSection', async () => {
  const elements = buttonService.getAllElements('.single-gate-login');
  const success = !!elements && elements.length > 0;

  // Handle visibility toggling
  elements?.forEach((section) => buttonService.toggleVisibility(section, true));

  return { success, sections: elements || [] };
});

export const attachWaitingSection = createAsyncThunk<
  { success: boolean; section: HTMLElement | undefined },
  void
>('auth/attachWaitingSection', async () => {
  const element = buttonService.getFirstElement('.single-gate-waiting');

  // Handle visibility toggling
  buttonService.toggleVisibility(element, false);

  return { success: !!element, section: element };
});

export const performLogin = createAsyncThunk<void, void, { state: RootState }>(
  'auth/performLogin',
  async (_, { getState }) => {
    const state = getState().auth;

    // Show waiting, hide login
    buttonService.toggleVisibility(state.waitingSection.value, true);
    state.loginSection.value?.forEach((section) =>
      buttonService.toggleVisibility(section, false, 'auth/performLogin')
    );
  }
);

export const handleLoginResult = createAsyncThunk<void, { success: boolean }, { state: RootState }>(
  'auth/handleLoginResult',
  async ({ success }, { getState }) => {
    const state = getState().auth;

    // Show/hide logout sections on success
    if (success) {
      state.logoutSection.value?.forEach((section) =>
        buttonService.toggleVisibility(section, true, 'auth/handleLoginResult')
      );
    }

    // Show login sections on failure, hide waiting
    state.loginSection.value?.forEach((section) =>
      buttonService.toggleVisibility(section, !success, 'auth/handleLoginResult')
    );
    buttonService.toggleVisibility(state.waitingSection.value, false);
  }
);

export const performLogout = createAsyncThunk<void, void, { state: RootState }>(
  'auth/performLogout',
  async (_, { getState }) => {
    const state = getState().auth;

    // Hide logout sections
    state.logoutSection.value?.forEach((section) =>
      buttonService.toggleVisibility(section, false, 'auth/performLogout')
    );
  }
);
